import React from "react";
import { Box, Typography, TextField, useTheme } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Product, ProductItemFormData } from "./types/products";
import { formFieldSx } from "../common/FormModal/FormModal";
import { MAX_ITEMS_PER_BATCH } from "./constants/productItemBatch";
import ProductItemBatchSummary from "./ProductItemBatchSummary";
import {
  getInventoryQuantityPrefix,
  getProductUnitLabel
} from "./utils/getProductInventoryStatCardDescription";

export interface AddProductItemQuantityStepProps {
  product: Product;
  formData: ProductItemFormData;
  quantity: number;
  onQuantityChange: (value: number) => void;
  quantityError?: string;
  disabled?: boolean;
}

const AddProductItemQuantityStep: React.FC<AddProductItemQuantityStepProps> = ({
  product,
  formData,
  quantity,
  onQuantityChange,
  quantityError,
  disabled = false
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  const catalogUnitLabel = getProductUnitLabel(product, t);
  const quantityUnitLabel = getInventoryQuantityPrefix(
    product.sellingMode,
    catalogUnitLabel,
    t
  );
  const perItemQty = formData.totalQuantity ?? 1;

  return (
    <Box
      data-testid="add-product-item-quantity-step"
      sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
    >
      <Typography variant="body2" color="text.secondary">
        {t(
          "add-product-item-wizard-quantity-description",
          "How many items do you need? Each item gets one QR code. This number is separate from the stock amount inside each item."
        )}
      </Typography>

      <TextField
        fullWidth
        size={matchMD ? "small" : "medium"}
        label={t(
          "add-product-item-wizard-number-of-rows",
          "How many items do you need?"
        )}
        type="number"
        value={quantity}
        onChange={(e) => {
          const parsed = parseInt(e.target.value, 10);
          onQuantityChange(Number.isNaN(parsed) ? 1 : parsed);
        }}
        error={!!quantityError}
        helperText={
          quantityError ??
          t(
            "add-product-item-wizard-quantity-helper",
            "Maximum {{max}} items per batch",
            { max: MAX_ITEMS_PER_BATCH }
          )
        }
        disabled={disabled}
        inputProps={{ min: 1, max: MAX_ITEMS_PER_BATCH, step: 1 }}
        sx={formFieldSx}
        data-testid="add-product-item-batch-quantity"
      />

      <ProductItemBatchSummary
        product={product}
        labelCount={quantity}
        perItemQuantity={perItemQty}
        quantityUnitLabel={quantityUnitLabel}
        variationName={formData.variationName}
        purchasePrice={formData.purchasePrice}
        salesPrice={formData.salesPrice}
      />
    </Box>
  );
};

export default AddProductItemQuantityStep;
